import React, { useState, useEffect, useRef, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import axios from "axios";
import * as XLSX from "xlsx";
import FRONTEND_CONFIG from "./frontend_config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
const ItemTypes = { QUESTION: "QUESTION", DROPPED: "DROPPED" };


const DraggableQuestion = ({ question, onAdd }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.QUESTION,
    item: { question },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));
  return (

<div
      ref={drag}
      className={`border p-4 rounded-xl bg-white shadow hover:shadow-md transition mb-2 flex justify-between items-start ${
        isDragging ? "opacity-60 scale-[0.98]" : "opacity-100"
      }`}
    >
      {/* LEFT CONTENT */}
      <div className="flex-1 pr-3">
        <h3 className="font-semibold text-gray-800 mb-1">
          {question.questionId && (
            <span className="text-blue-600 font-bold mr-1">
              {question.questionId}
            </span>
          )}
          {question.questionText}
        </h3>

        <ul className="space-y-1">
          {question.options?.map((opt, i) => (
            <li
              key={i}
              className={`p-2 rounded-md border ${
                opt.optionText === question.correctAnswer
                  ? "bg-green-100 border-green-400 text-green-700"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              {String.fromCharCode(65 + i)}. {opt.optionText}
            </li>
          ))}
        </ul>
      </div>


      
       {/* ✅ RIGHT ARROW BUTTON */}
      <div className="flex flex-col items-center">
        <button
          onClick={() => onAdd(question)}
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-9 h-9 flex items-center justify-center text-lg shadow"
          title="Add to Question Set"
        >
          ➡️
        </button>
      </div>
    </div>
  );
};

// Draggable item inside the Question Set (for reordering)
const DraggableDroppedQuestion = ({ q, index, moveItem, onRemove, onPreview }) => {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: ItemTypes.DROPPED,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.DROPPED,
    item: { _id: q._id, index },
    collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
  }));

  drag(drop(ref));

  return (
    <li
      ref={ref}
      className={`bg-white p-4 rounded-xl shadow border hover:shadow-md transition flex justify-between items-start cursor-grab ${
        isDragging ? "opacity-60" : "opacity-100"
      }`}
    >
      <div onClick={() => onPreview(q)} className="flex-1 pr-3">
       <p className="font-medium text-gray-800">
  {index + 1}.{" "}
  {q.questionId && (
    <span className="text-blue-600 font-bold mr-1">
      {q.questionId}
    </span>
  )}
  {q.questionText}
</p>

      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => onPreview(q)}
          className="text-sm px-2 py-1 border rounded-md hover:bg-gray-50"
          title="Preview"
        >
          👁️
        </button>
        <button
          onClick={() => onRemove(q._id)}
          className="text-red-500 hover:text-red-700 font-bold text-xl px-2"
          title="Remove"
        >
          −
        </button>
      </div>
    </li>
  );
};

// 🧾 Drop zone for selected questions (supports reorder)
const QuestionSetDropZone = ({ onDrop, droppedQuestions, setDroppedQuestions, onPreview }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.QUESTION,
    drop: (item) => onDrop(item.question),
    collect: (monitor) => ({ isOver: !!monitor.isOver() }),
  }));

  const moveItem = useCallback((fromIndex, toIndex) => {
    setDroppedQuestions((prev) => {
      const copy = [...prev];
      const [moved] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, moved);
      return copy;
    });
  }, [setDroppedQuestions]);

  const handleRemove = (id) => onDrop({ _id: id, remove: true });

  return (
    <div
      ref={drop}
      className={`flex-1 p-4 border-2 border-dashed rounded-xl min-h-[80vh] transition relative ${
        isOver ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-100"
      }`}
    >
      <h3 className="text-lg font-bold mb-4 text-gray-700">📄 Question Set</h3>
      {droppedQuestions.length === 0 ? (
        <p className="text-gray-400 italic">Drag questions here...</p>
      ) : (
        <ul className="space-y-4">
          {droppedQuestions.map((q, i) => (
            <DraggableDroppedQuestion
              key={q._id}
              q={q}
              index={i}
              moveItem={moveItem}
              onRemove={handleRemove}
              onPreview={onPreview}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

// Utility: CSV export
const downloadCSV = (filename, rows) => {
  const csv = rows
    .map((r) =>
      r
        .map((cell) => {
          if (cell === null || cell === undefined) return "";
          const s = String(cell).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Print / PDF helper (opens new window for printing)
const printQuestionSet = (questionSetName, questions) => {
  const html = `
    <html>
    <head>
      <title>${questionSetName}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;padding:20px;} .q{margin-bottom:16px}</style>
    </head>
    <body>
      <h1>${questionSetName}</h1>
      ${questions
        .map(
          (q, i) => `
        <div class="q">
          <strong>${i + 1}. ${q.questionText}</strong>
          <ul>
            ${q.options?.map((o, idx) => `<li>${String.fromCharCode(65 + idx)}. ${o.optionText}</li>`).join("")}
          </ul>
        </div>`
        )
        .join("")}
    </body>
    </html>`;
  const w = window.open("", "_blank", "noopener,noreferrer");
  w.document.write(html);
  w.document.close();
  w.focus();
  // give browser a tick then print
  setTimeout(() => w.print(), 500);
};

// 🧠 Main page
export default function CreateQuestionSet() {
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionSetName, setQuestionSetName] = useState("");
  const [previewQuestion, setPreviewQuestion] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [existingSets, setExistingSets] = useState([]); // optional: for duplicate UI
  const [showImportMenu, setShowImportMenu] = useState(false);
  const importRef = useRef(null);
  const [alertData, setAlertData] = useState({ show: false, message: "" });
  const showAlert = (msg) => {
    setAlertData({ show: true, message: msg });
    setTimeout(() => {
      setAlertData({ show: false, message: "" });
    }, 1800);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const exam = location.state?.exam;
  const { questionSetId } = useParams(); // ← Add this line here

  useEffect(() => {
  const handler = (e) => {
    const q = e.detail;
    setSelectedQuestions((prev) =>
      prev.some((item) => item._id === q._id) ? prev : [...prev, q]
    );
  };

  window.addEventListener("ADD_TO_SET", handler);
  return () => window.removeEventListener("ADD_TO_SET", handler);
}, []);


useEffect(() => {
  function handleClickOutside(event) {
    if (importRef.current && !importRef.current.contains(event.target)) {
      setShowImportMenu(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  useEffect(() => {
    if (questionSetId) {
      axios.get(`${FRONTEND_CONFIG.API_URL}/api/questionset/${questionSetId}`)
        .then(res => {
          const set = res.data;
          setQuestionSetName(set.questionSetName);
          setSelectedQuestions(set.questions || []);
        })
        .catch(err => console.error(err));
    }
  }, [questionSetId]);
  useEffect(() => {
    if (!exam) {
      showAlert("⚠️ Exam data missing. Redirecting back to exam list.");
      navigate("/exam-list");
    }
  }, [exam, navigate]);

  const parseFileAndUpload = async (file, type) => {
  if (type === "excel") {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!rows || rows.length === 0) {
        showAlert("No rows found in the file.");
        return;
      }

      let addedCount = 0;

      for (const row of rows) {
        const optionsArr = (row.options || "")
          .toString()
          .split("|")
          .map((o) => o.trim())
          .filter(Boolean);

        const payload = {
          questionText: row.question || row.questionText || "",
          options: optionsArr.map((opt) => ({ optionText: opt })),
          correctAnswer: row.correctAnswer || "",
          marks: row.marks || 1,
          subject: row.subject || "",
          level: row.level || "",
          chapter: row.topic || row.chapter || "",
          class: row.className || row.class || "",
          createdBy: "671d8d9c0b6e8a9e145efbc2",
        };

        if (!payload.questionText || payload.options.length === 0) continue;

        try {
          await axios.post(`${FRONTEND_CONFIG.API_URL}/api/question/add-question`, payload);
          addedCount++;
        } catch (err) {
          console.error("Failed to save row:", err, row);
        }
      }

      showAlert(`✅ Imported ${addedCount} questions`);

     const res = await axios.get(`${FRONTEND_CONFIG.API_URL}/api/question/all-question`);
window.__ALL_BANK_Q__ = res.data;
window.__FILTERED_BANK_Q__ = res.data;
    } catch (err) {
      console.error(err);
      showAlert("❌ Failed to import questions");
    }
  } else {
    // fallback: PDF / Word uploaded to backend as before
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    try {
      const res = await axios.post(
        `${FRONTEND_CONFIG.API_URL}/api/question/import`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      console.log("IMPORT RESPONSE:", res.data);
      showAlert(`✅ Imported ${res.data.count || 0} questions`);
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      console.error("IMPORT ERROR FULL:", err);
      const msg =
        err.response?.data?.message || err.response?.data || err.message || "Failed to import questions!";
      showAlert(`❌ ${msg}`);
    }
  }
};

// File input handlers
const handleImportExcel = (e) => {
  const file = e.target.files[0];
  if (file) parseFileAndUpload(file, "excel");
};

const handleImportPDF = (e) => {
  const file = e.target.files[0];
  if (file) parseFileAndUpload(file, "pdf");
};

const handleImportWord = (e) => {
  const file = e.target.files[0];
  if (file) parseFileAndUpload(file, "word");
};

// ⭐ Move All (unchanged)
const moveAllVisibleQuestions = () => {
  const list = window.__FILTERED_BANK_Q__ || window.__ALL_BANK_Q__ || [];
  if (!list || list.length === 0) {
    showAlert("No questions available to add.");
    return;
  }
  setSelectedQuestions((prev) => {
    const prevIds = new Set(prev.map((p) => p._id));
    const toAdd = list.filter((q) => !prevIds.has(q._id));
    if (toAdd.length === 0) {
      showAlert("All visible questions are already added.");
      return prev;
    }
    showAlert(`Added ${toAdd.length} questions`);
    return [...prev, ...toAdd];
  });
};
  const handleDrop = (question) => {
    // If remove is requested
    if (question.remove) {
      setSelectedQuestions((prev) => prev.filter((q) => q._id !== question._id));
      return;
    }

    // Normal add
    setSelectedQuestions((prev) => (prev.some((q) => q._id === question._id) ? prev : [...prev, question]));
  };

  // ✅ Save Question Set
  const handleSaveQuestionSet = async () => {
    if (!questionSetName.trim()) {
      showAlert("Please enter Question Set Name");

      return;
    }
    if (selectedQuestions.length === 0) {
      showAlert("Please add at least one question!");
      return;
    }

    try {
      const payload = {
        examId: exam?._id,
        examName: exam?.title,
        questionSetName,
        questionIds: selectedQuestions.map((q) => q._id),
      };

      console.log("📦 Sending payload:", payload);

      if (questionSetId) {
        // ✅ Editing existing set
        await axios.put(`${FRONTEND_CONFIG.API_URL}/api/questionset/${questionSetId}`, payload);
        showAlert("✅ Question Set updated successfully!");
      } else {
        // ✅ Creating new set
        await axios.post(`${FRONTEND_CONFIG.API_URL}/api/questionset/save`, payload);
        showAlert("✅ Question Set saved successfully!");
      }

      setQuestionSetName("");
      setSelectedQuestions([]);
    } catch (err) {
      console.error("Error saving question set:", err.response?.data || err);
      showAlert("Failed to save question set!");
    }
  };

  const handlePrint = () => {
    if (!selectedQuestions || selectedQuestions.length === 0) {
      showAlert("No questions to print!");
      return;
    }
    const printContent = `
      <html>
        <head>
          <title>${questionSetName || "Question Set"}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .question { margin-bottom: 20px; }
            .options { margin-left: 20px; }
            .options div { margin-bottom: 4px; }
          </style>
        </head>
        <body>
          <h2>${questionSetName}</h2>
          ${selectedQuestions
            .map(
              (q, index) => `
            <div class="question">
              <strong>${index + 1}. ${q.questionText}</strong>
              <div class="options">
                ${q.options
                  .map(
                    (opt) => `<div>- ${opt.optionText}</div>`
                  )
                  .join("")}
              </div>
            </div>
          `
            )
            .join("")}
        </body>
      </html>
    `;

    const printWindow = window.open("", "", "width=800,height=900");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePreview = (q) => setPreviewQuestion(q);
  const closePreview = () => setPreviewQuestion(null);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
        {/* Left side - Question Bank */}
        <div className="w-full lg:w-1/2 border-r border-gray-300 overflow-y-auto p-6">
          <h2 className="text-2xl font-bold mb-4 text-blue-700">🧠 Question Bank</h2>
         <div className="flex items-center gap-3 mb-4">

  {/* Move All Button - small, compact */}
  <button
    onClick={moveAllVisibleQuestions}
    className="bg-orange-500 text-white px-3 py-1.5 rounded-md text-sm hover:bg-orange-600 flex items-center gap-1"
  >
    ➡️ Move All
  </button>

  {/* Import Dropdown */}
  {/* Single Import Button */}
<div className="relative" ref={importRef}>
  <button
    onClick={() => document.getElementById("fileImport").click()}
    className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700 flex items-center gap-1"
  >
    📤 Import File
  </button>
  <input
    id="fileImport"
    type="file"
    accept=".xlsx,.xls,.pdf,.doc,.docx"
    hidden
    onChange={(e) => {
      const file = e.target.files[0];
      if (!file) return;
      // Detect file type based on extension
      const ext = file.name.split(".").pop().toLowerCase();
      let type = "";
      if (["xls", "xlsx"].includes(ext)) type = "excel";
      else if (ext === "pdf") type = "pdf";
      else if (["doc", "docx"].includes(ext)) type = "word";
      else {
        showAlert("❌ Unsupported file type!");
        return;
      }
      parseFileAndUpload(file, type);
      e.target.value = "";
    }}
  />
</div>
</div>
          <QuestionBankWrapper />
        </div>
        <div className="w-full lg:w-1/2 p-6 overflow-y-auto">
          <div className="mb-4 flex flex-col gap-3">
            <input
              type="text"
              placeholder="Enter Question Set Name"
              value={questionSetName}
              onChange={(e) => { setQuestionSetName(e.target.value); setIsDuplicating(false); }}
              className="border border-gray-300 rounded-lg p-2 w-full focus:ring focus:ring-blue-300"
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleSaveQuestionSet}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                💾 Save Question Set
              </button>

              {questionSetId && (
                <button
                  onClick={handleSaveQuestionSet}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  ✏️ Update Question Set
                </button>
              )}

              <button
                onClick={handlePrint}
                className="bg-gray-200 px-3 py-2 rounded-md hover:bg-gray-300"
              >
                📄 Print / PDF
              </button>
            </div>

            {/* Hidden file inputs (⭐ ADDED) */}
            <input id="fileExcel" type="file" accept=".xlsx,.xls" hidden onChange={handleImportExcel} />
            <input id="filePDF" type="file" accept=".pdf" hidden onChange={handleImportPDF} />
            <input id="fileWord" type="file" accept=".doc,.docx" hidden onChange={handleImportWord} />
          </div>

          <QuestionSetDropZone
            onDrop={handleDrop}
            droppedQuestions={selectedQuestions}
            setDroppedQuestions={setSelectedQuestions}
            onPreview={handlePreview}
          />
        </div>

        {/* Preview Modal */}
        {previewQuestion && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-11/12 md:w-3/5 max-h-[80vh] overflow-auto">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold">Question Preview</h3>
                <button onClick={closePreview} className="text-gray-500">✕</button>
              </div>
              <div>
                <p className="font-semibold mb-2">{previewQuestion.questionText}</p>
                <ul className="list-disc pl-6 space-y-2">
                  {previewQuestion.options?.map((o, idx) => (
                    <li key={idx} className={`${o.optionText === previewQuestion.correctAnswer ? 'font-semibold' : ''}`}>
                      {String.fromCharCode(65 + idx)}. {o.optionText}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 text-sm text-gray-600">
                  <div><strong>Subject:</strong> {previewQuestion.subject || '-'}</div>
                  <div><strong>Class:</strong> {previewQuestion.className || '-'}</div>
                  <div><strong>Topic:</strong> {previewQuestion.topic || '-'}</div>
                  <div><strong>Level:</strong> {previewQuestion.level || '-'}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CENTER ALERT ADDED BELOW */}
        {alertData.show && (
          <div className="fixed inset-0 flex items-center justify-center z-[9999]">
            <div className="bg-black text-white px-6 py-3 rounded-lg shadow-xl text-center animate-fadeIn">
              {alertData.message}
            </div>
          </div>
        )}

        <style>
          {`
        @keyframes fadeInScale {
          0% { opacity: 0; transform: scale(0.8); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeInScale 0.25s ease-out;
        }
        `}
        </style>
      </div>
    </DndProvider>
  );
}
const handleAddClick = (question) => {
  const existing = window.__SELECTED_Q__ || [];
  if (existing.some((q) => q._id === question._id)) return;

  const updated = [...existing, question];
  window.__SELECTED_Q__ = updated;

  // Notify main component via custom event
  window.dispatchEvent(new CustomEvent("ADD_TO_SET", { detail: question }));
};


// 🧱 Question Bank Loader with Search
const QuestionBankWrapper = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${FRONTEND_CONFIG.API_URL}/api/question/all-question`);
        setQuestions(res.data || []);

        // ⭐ ADDED: expose bank questions globally for "Move All Visible" feature
        // store full list and filtered list on window so the top-level component can access without prop drilling
        window.__ALL_BANK_Q__ = res.data || [];
        // initial filtered list same as all
        window.__FILTERED_BANK_Q__ = res.data || [];
      } catch (err) {
        console.error("Error fetching questions:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  const filtered = questions.filter((q) => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return (
      (q.questionText || "").toLowerCase().includes(s) ||
      (q.subject || "").toLowerCase().includes(s) ||
      (q.className || "").toLowerCase().includes(s) ||
      (q.topic || "").toLowerCase().includes(s) ||
      (q.level || "").toLowerCase().includes(s)
    );
  });

  // ⭐ ADDED: update global filtered list whenever filtered result changes
  useEffect(() => {
    window.__FILTERED_BANK_Q__ = filtered || [];
  }, [filtered]);

    if (loading) return <p className="text-gray-500">Loading questions...</p>;

  return (
    <div>
      {/* Search Bar */}
      <input
        type="text"
        placeholder="Search questions..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="border p-2 rounded-lg w-full mb-4"
      />

      {/* Question List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <p className="text-gray-400 italic">No questions found.</p>
        ) : (
          // filtered.map((q) => <DraggableQuestion key={q._id} question={q} />)
          filtered.map((q) => (
  <DraggableQuestion key={q._id} question={q} onAdd={handleAddClick} />
))

        )}
      </div>
    </div>
  );
};
